import type { Invoice, CompanyProfile, InvoiceTrip } from '@coreledger/shared-types';

function esc(s: string | undefined | null): string {
  return (s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function fmt(n: number): string {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function toWords(n: number): string {
  const rupees = Math.floor(n);
  const paise = Math.round((n - rupees) * 100);

  function chunk(num: number): string {
    if (num === 0) return '';
    if (num < 20) return ONES[num] + ' ';
    if (num < 100) return TENS[Math.floor(num / 10)] + (num % 10 ? ' ' + ONES[num % 10] : '') + ' ';
    return ONES[Math.floor(num / 100)] + ' Hundred ' + chunk(num % 100);
  }

  function convert(num: number): string {
    if (num === 0) return 'Zero';
    let result = '';
    if (num >= 10000000) { result += chunk(Math.floor(num / 10000000)) + 'Crore '; num %= 10000000; }
    if (num >= 100000) { result += chunk(Math.floor(num / 100000)) + 'Lakh '; num %= 100000; }
    if (num >= 1000) { result += chunk(Math.floor(num / 1000)) + 'Thousand '; num %= 1000; }
    result += chunk(num);
    return result.trim();
  }

  let words = convert(rupees) + ' Rupees';
  if (paise > 0) words += ' and ' + convert(paise) + ' Paise';
  return words + ' Only';
}

type FormatTheme = {
  primary: string; badgeBg: string; badgeText: string;
  tableHeaderBg: string; tableHeaderText: string; accentBg: string;
  wordsBg: string; wordsBorder: string; wordsText: string; borderBottom: string;
};

const THEMES: Record<string, FormatTheme> = {
  classic: {
    primary: '#1d4ed8', badgeBg: '#1d4ed8', badgeText: 'white',
    tableHeaderBg: '#1d4ed8', tableHeaderText: 'white', accentBg: '#f8fafc',
    wordsBg: '#eff6ff', wordsBorder: '#bfdbfe', wordsText: '#1e40af', borderBottom: '2px solid #1d4ed8',
  },
  modern: {
    primary: '#1e293b', badgeBg: '#1e293b', badgeText: '#e2e8f0',
    tableHeaderBg: '#1e293b', tableHeaderText: '#f1f5f9', accentBg: '#f8fafc',
    wordsBg: '#f1f5f9', wordsBorder: '#cbd5e1', wordsText: '#334155', borderBottom: '3px solid #1e293b',
  },
  minimal: {
    primary: '#374151', badgeBg: '#f3f4f6', badgeText: '#111827',
    tableHeaderBg: '#f3f4f6', tableHeaderText: '#111827', accentBg: '#f9fafb',
    wordsBg: '#f9fafb', wordsBorder: '#d1d5db', wordsText: '#374151', borderBottom: '1px solid #d1d5db',
  },
  elegant: {
    primary: '#047857', badgeBg: '#047857', badgeText: 'white',
    tableHeaderBg: '#059669', tableHeaderText: 'white', accentBg: '#f0fdf4',
    wordsBg: '#ecfdf5', wordsBorder: '#6ee7b7', wordsText: '#065f46', borderBottom: '2px solid #047857',
  },
  bold: {
    primary: '#b45309', badgeBg: '#d97706', badgeText: 'white',
    tableHeaderBg: '#d97706', tableHeaderText: 'white', accentBg: '#fffbeb',
    wordsBg: '#fffbeb', wordsBorder: '#fcd34d', wordsText: '#92400e', borderBottom: '2px solid #d97706',
  },
};

const DOC_LABELS: Record<string, string> = {
  tax_invoice: 'TAX INVOICE',
  bill_of_supply: 'INVOICE',
  quotation: 'QUOTATION',
  credit_note: 'CREDIT NOTE',
  debit_note: 'DEBIT NOTE',
  lorry_receipt: 'LORRY RECEIPT',
};

export function buildInvoiceHtml(
  invoice: Invoice,
  profile: CompanyProfile | null,
  format = 'classic',
  watermark = '',
  logoBase64 = ''
): string {
  const t = THEMES[format] ?? THEMES.classic;
  const docType = invoice.docType ?? 'tax_invoice';
  const isBOS = docType === 'bill_of_supply';
  const isIgst = invoice.taxType === 'igst';
  const docLabel = DOC_LABELS[docType] ?? 'TAX INVOICE';
  const dueDateLabel = docType === 'quotation' ? 'Valid Till' : 'Due Date';

  const descCell = (item: typeof invoice.items[0]) => {
    const desc = esc(item.description) || '<span style="color:#94a3b8;font-style:italic;">—</span>';
    return item.notes
      ? `${desc}<br><span style="font-size:10px;color:#64748b;">${esc(item.notes)}</span>`
      : desc;
  };

  const isRcm = invoice.isRcm === true;

  const itemRows = invoice.items.map((item) => {
    if (isBOS) {
      return `
      <tr>
        <td>${descCell(item)}</td>
        <td class="c">${esc(item.hsnSac)}</td>
        <td class="r">${item.qty}</td>
        <td class="r">₹${fmt(item.rate)}</td>
        <td class="r">₹${fmt(item.taxableValue)}</td>
      </tr>`;
    }
    if (isIgst) {
      const igst = isRcm ? 0 : item.taxableValue * item.gstRate / 100;
      return `
      <tr>
        <td>${descCell(item)}</td>
        <td class="c">${esc(item.hsnSac)}</td>
        <td class="r">${item.qty}</td>
        <td class="r">₹${fmt(item.rate)}</td>
        <td class="r">₹${fmt(item.taxableValue)}</td>
        <td class="c">${item.gstRate}%</td>
        <td class="r">₹${fmt(igst)}</td>
        <td class="r">₹${fmt(item.taxableValue)}</td>
      </tr>`;
    }
    const cgst = isRcm ? 0 : item.taxableValue * item.gstRate / 2 / 100;
    const sgst = cgst;
    return `
      <tr>
        <td>${descCell(item)}</td>
        <td class="c">${esc(item.hsnSac)}</td>
        <td class="r">${item.qty}</td>
        <td class="r">₹${fmt(item.rate)}</td>
        <td class="r">₹${fmt(item.taxableValue)}</td>
        <td class="c">${item.gstRate}%</td>
        <td class="r">₹${fmt(cgst)}</td>
        <td class="r">₹${fmt(sgst)}</td>
        <td class="r">₹${fmt(item.taxableValue)}</td>
      </tr>`;
  }).join('');

  const igstTotal = isRcm ? 0 : invoice.items.reduce((s, i) => s + i.taxableValue * i.gstRate / 100, 0);
  const cgstTotal = isRcm ? 0 : invoice.items.reduce((s, i) => s + i.taxableValue * i.gstRate / 2 / 100, 0);
  const sgstTotal = cgstTotal;

  const addr = [profile?.addressLine1, profile?.addressLine2, profile?.city, profile?.state, profile?.postalCode]
    .filter(Boolean).join(', ');

  const wmFontSize = watermark.length <= 5 ? 130 : watermark.length <= 8 ? 110
    : watermark.length <= 12 ? 90 : watermark.length <= 16 ? 72
    : watermark.length <= 22 ? 56 : 44;
  const wmLetterSpacing = watermark.length <= 8 ? 10 : watermark.length <= 16 ? 6 : 3;
  const watermarkHtml = watermark
    ? `<div class="watermark"><span style="font-size:${wmFontSize}px;letter-spacing:${wmLetterSpacing}px;">${esc(watermark)}</span></div>`
    : '';

  const logoHtml = logoBase64
    ? `<img src="${logoBase64}" alt="Logo" style="max-height:60px;max-width:160px;object-fit:contain;margin-bottom:6px;display:block;" />`
    : '';

  // IRN / EWB compliance block — shown only when IRN has been generated
  const irnHtml = invoice.irn ? `
  <div style="margin-bottom:16px;padding:10px 14px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;font-size:10px;color:#166534;">
    <div style="display:flex;gap:24px;flex-wrap:wrap;align-items:flex-start;">
      <div style="flex:1;min-width:200px;">
        <div style="font-weight:700;font-size:9px;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px;color:#15803d;">E-Invoice Details</div>
        <div><span style="color:#4b5563;">IRN:</span> <span style="font-family:monospace;word-break:break-all;">${esc(invoice.irn)}</span></div>
        ${invoice.ackNo ? `<div style="margin-top:2px;"><span style="color:#4b5563;">Ack No:</span> ${esc(invoice.ackNo)}</div>` : ''}
        ${invoice.ackDate ? `<div style="margin-top:2px;"><span style="color:#4b5563;">Ack Date:</span> ${esc(invoice.ackDate)}</div>` : ''}
        ${invoice.ewbNo ? `<div style="margin-top:6px;font-weight:700;font-size:9px;text-transform:uppercase;letter-spacing:.5px;color:#15803d;">E-Way Bill</div>` : ''}
        ${invoice.ewbNo ? `<div><span style="color:#4b5563;">EWB No:</span> ${esc(invoice.ewbNo)}</div>` : ''}
        ${invoice.ewbDate ? `<div style="margin-top:2px;"><span style="color:#4b5563;">Valid From:</span> ${esc(invoice.ewbDate)}</div>` : ''}
        ${invoice.ewbValidUpto ? `<div style="margin-top:2px;"><span style="color:#4b5563;">Valid Upto:</span> ${esc(invoice.ewbValidUpto)}</div>` : ''}
      </div>
      ${invoice.qrCode ? `<div style="flex-shrink:0;"><img src="${invoice.qrCode}" alt="IRN QR" style="width:80px;height:80px;display:block;" /></div>` : ''}
    </div>
  </div>` : '';

  const hasBankDetails = profile && (profile.bankName || profile.accountNumber || profile.upiId);
  const bankHtml = hasBankDetails ? `
  <div class="bank-details">
    <strong>Bank Details</strong>
    <div class="bank-grid">
      ${profile.bankName ? `<span class="bk-lbl">Bank</span><span>${esc(profile.bankName)}</span>` : ''}
      ${profile.accountName ? `<span class="bk-lbl">Name</span><span>${esc(profile.accountName)}</span>` : ''}
      ${profile.accountNumber ? `<span class="bk-lbl">A/c No.</span><span style="font-family:monospace;">${esc(profile.accountNumber)}</span>` : ''}
      ${profile.ifscCode ? `<span class="bk-lbl">IFSC</span><span style="font-family:monospace;">${esc(profile.ifscCode)}</span>` : ''}
      ${profile.upiId ? `<span class="bk-lbl">UPI ID</span><span>${esc(profile.upiId)}</span>` : ''}
    </div>
  </div>` : '';

  const tableHeader = isBOS ? `
    <tr>
      <th style="text-align:left;width:38%">Description</th>
      <th class="c" style="width:11%">HSN/SAC</th>
      <th class="r" style="width:8%">Qty</th>
      <th class="r" style="width:13%">Rate</th>
      <th class="r" style="width:15%">Amount</th>
    </tr>` : `
    <tr>
      <th style="text-align:left;width:28%">Description</th>
      <th class="c" style="width:9%">HSN/SAC</th>
      <th class="r" style="width:6%">Qty</th>
      <th class="r" style="width:10%">Rate</th>
      <th class="r" style="width:13%">Taxable Value</th>
      <th class="c" style="width:7%">GST%</th>
      ${isIgst
        ? `<th class="r" style="width:17%">IGST</th>`
        : `<th class="r" style="width:9%">CGST</th><th class="r" style="width:9%">SGST</th>`}
      <th class="r" style="width:10%">Amount</th>
    </tr>`;

  const advanceReceived = invoice.advanceReceived ?? 0;
  const tdsRate = invoice.tdsRate ?? 0;
  const tcsRate = invoice.tcsRate ?? 0;
  const tdsAmount = tdsRate > 0 ? Math.round(invoice.subtotal * tdsRate / 100) : 0;
  const tcsAmount = tcsRate > 0 ? Math.round(invoice.subtotal * tcsRate / 100) : 0;
  const netPayable = invoice.total + tcsAmount - advanceReceived;

  const extraRows = `
    ${tcsRate > 0 ? `<tr><td>TCS @ ${tcsRate}%</td><td>₹${fmt(tcsAmount)}</td></tr>` : ''}
    ${advanceReceived > 0 ? `<tr><td>Advance Received</td><td>- ₹${fmt(advanceReceived)}</td></tr>` : ''}
    ${(advanceReceived > 0 || tcsRate > 0) ? `<tr style="font-weight:700;color:${t.primary};"><td>Net Payable</td><td>₹${fmt(netPayable)}</td></tr>` : ''}
    ${tdsRate > 0 ? `<tr style="font-size:10px;color:#64748b;"><td>TDS deductible @ ${tdsRate}% (by customer)</td><td>₹${fmt(tdsAmount)}</td></tr>` : ''}
  `;

  const rcmTaxLabel = isRcm ? ' (Payable by Recipient — RCM)' : '';
  const totalsHtml = isBOS ? `
  <div class="totals">
    <table>
      <tr><td>Total Amount</td><td>₹${fmt(invoice.total)}</td></tr>
      ${extraRows}
    </table>
  </div>` : `
  <div class="totals">
    <table>
      <tr><td>Taxable Amount</td><td>₹${fmt(invoice.subtotal)}</td></tr>
      ${isIgst
        ? `<tr><td>IGST${rcmTaxLabel}</td><td>₹${fmt(igstTotal)}</td></tr>`
        : `<tr><td>CGST${rcmTaxLabel}</td><td>₹${fmt(cgstTotal)}</td></tr><tr><td>SGST${rcmTaxLabel}</td><td>₹${fmt(sgstTotal)}</td></tr>`}
      <tr style="font-weight:700;"><td>Total Amount</td><td>₹${fmt(isRcm ? invoice.subtotal : invoice.total)}</td></tr>
      ${extraRows}
    </table>
  </div>`;

  // RCM mandatory declaration
  const rcmDeclaration = isRcm ? `
  <div style="margin-bottom:16px;padding:10px 14px;background:#fef2f2;border:1px solid #fca5a5;border-radius:6px;font-size:10.5px;color:#991b1b;">
    <strong style="display:block;margin-bottom:4px;">⚠ Reverse Charge Declaration</strong>
    Reverse charge is applicable on this supply. Tax is payable by the recipient of the supply as per Section 9(3) / 9(4) of the CGST Act, 2017. The supplier is NOT liable to pay GST on this transaction.
  </div>` : '';

  // Build effective trips list for transport details section
  const effectiveTrips: InvoiceTrip[] = invoice.trips?.length
    ? invoice.trips
    : (invoice.vehicleNumber || invoice.lrNumber || invoice.fromCity || invoice.toCity || invoice.driverName)
      ? [{ vehicleNumber: invoice.vehicleNumber, lrNumber: invoice.lrNumber, fromCity: invoice.fromCity, toCity: invoice.toCity, driverName: invoice.driverName }]
      : [];

  const transportSectionLabel = docType === 'lorry_receipt' ? 'Transportation Details' : 'LR / Transport Details';
  const hasFreight = effectiveTrips.some((t) => (t.freightAmount ?? 0) > 0);
  const totalFreight = effectiveTrips.reduce((s, t) => s + (t.freightAmount ?? 0), 0);
  const transportSectionHtml = effectiveTrips.length > 0 ? `
  <div style="margin-bottom:16px;border:1px solid #bae6fd;border-radius:6px;overflow:hidden;">
    <div style="background:#0ea5e9;color:white;padding:6px 12px;font-size:10px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;">${transportSectionLabel}</div>
    <table style="width:100%;border-collapse:collapse;font-size:10px;">
      <thead>
        <tr style="background:#f0f9ff;">
          <th style="padding:5px 8px;text-align:center;border-bottom:1px solid #bae6fd;width:4%">#</th>
          <th style="padding:5px 8px;text-align:left;border-bottom:1px solid #bae6fd;">LR No.</th>
          <th style="padding:5px 8px;text-align:left;border-bottom:1px solid #bae6fd;">LR Date</th>
          <th style="padding:5px 8px;text-align:left;border-bottom:1px solid #bae6fd;">Vehicle No.</th>
          <th style="padding:5px 8px;text-align:left;border-bottom:1px solid #bae6fd;">From</th>
          <th style="padding:5px 8px;text-align:left;border-bottom:1px solid #bae6fd;">To</th>
          <th style="padding:5px 8px;text-align:left;border-bottom:1px solid #bae6fd;">Driver</th>
          ${hasFreight ? `<th style="padding:5px 8px;text-align:right;border-bottom:1px solid #bae6fd;">Freight (₹)</th>` : ''}
        </tr>
      </thead>
      <tbody>
        ${effectiveTrips.map((trip, idx) => `
        <tr style="${idx % 2 === 1 ? 'background:#f0f9ff;' : ''}">
          <td style="padding:4px 8px;border-bottom:1px solid #e0f2fe;text-align:center;">${idx + 1}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #e0f2fe;font-family:monospace;font-weight:600;">${esc(trip.lrNumber) || '—'}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #e0f2fe;">${esc(trip.lrDate) || '—'}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #e0f2fe;font-family:monospace;">${esc(trip.vehicleNumber) || '—'}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #e0f2fe;">${esc(trip.fromCity) || '—'}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #e0f2fe;">${esc(trip.toCity) || '—'}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #e0f2fe;">${esc(trip.driverName) || '—'}</td>
          ${hasFreight ? `<td style="padding:4px 8px;border-bottom:1px solid #e0f2fe;text-align:right;">${(trip.freightAmount ?? 0) > 0 ? `₹${fmt(trip.freightAmount!)}` : '—'}</td>` : ''}
        </tr>`).join('')}
      </tbody>
      ${hasFreight && effectiveTrips.length > 1 ? `
      <tfoot>
        <tr style="background:#e0f2fe;font-weight:700;">
          <td colspan="7" style="padding:5px 8px;text-align:right;border-top:1px solid #7dd3fc;">Total Freight</td>
          <td style="padding:5px 8px;text-align:right;border-top:1px solid #7dd3fc;">₹${fmt(totalFreight)}</td>
        </tr>
      </tfoot>` : ''}
    </table>
  </div>` : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${docLabel} ${esc(invoice.invoiceNumber)}</title>
<style>
  @page { margin: 18px 24px; }
  :root { color-scheme: light; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 12px; color: #1a1a1a; background: #ffffff; padding: 24px; }
  .watermark {
    position: fixed; top: 18px; left: 24px; right: 24px; bottom: 18px;
    display: flex; align-items: center; justify-content: center;
    pointer-events: none; z-index: 9999;
  }
  .watermark span {
    display: block; width: 80%; font-weight: 900;
    color: rgba(0,0,0,0.10); white-space: normal; word-break: break-word;
    transform: rotate(-35deg);
    text-transform: uppercase; text-align: center;
    user-select: none;
  }
  h1 { font-size: 22px; font-weight: 700; color: ${t.primary}; letter-spacing: 1px; }
  h2 { font-size: 14px; font-weight: 600; margin-bottom: 4px; }
  .header { width: 100%; border-collapse: collapse; margin-bottom: 0; }
  .header td { vertical-align: top; padding-bottom: 16px; border-bottom: ${t.borderBottom}; }
  .header-spacer { height: 24px; }
  .company p { color: #444; line-height: 1.5; margin-top: 4px; font-size: 11px; }
  .invoice-meta { text-align: right; }
  .invoice-meta .badge { background: ${t.badgeBg}; color: ${t.badgeText}; padding: 5px 14px; border-radius: 4px; font-size: 11px; font-weight: 700; letter-spacing: 1px; margin-bottom: 8px; display: inline-block; }
  .invoice-meta .num { font-size: 18px; font-weight: 700; color: ${t.primary}; margin-bottom: 6px; }
  .invoice-meta table { margin-left: auto; border-collapse: collapse; }
  .invoice-meta td { padding: 2px 0 2px 12px; }
  .invoice-meta td:first-child { color: #666; font-size: 11px; }
  .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 20px; }
  .box { background: ${t.accentBg}; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px 14px; }
  .box h2 { color: ${t.primary}; font-size: 10px; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 6px; }
  .box p { color: #1a1a1a; line-height: 1.6; }
  table.items { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
  table.items th { background: ${t.tableHeaderBg}; color: ${t.tableHeaderText}; padding: 7px 8px; font-size: 11px; font-weight: 600; }
  table.items td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; font-size: 11px; }
  table.items tr:nth-child(even) td { background: ${t.accentBg}; }
  .c { text-align: center; } .r { text-align: right; }
  .totals { display: flex; justify-content: flex-end; margin-bottom: 16px; }
  .totals table { border-collapse: collapse; min-width: 280px; }
  .totals td { padding: 5px 10px; font-size: 12px; }
  .totals tr:last-child td { border-top: 2px solid ${t.primary}; font-weight: 700; font-size: 13px; color: ${t.primary}; padding-top: 8px; }
  .totals td:last-child { text-align: right; }
  .words { background: ${t.wordsBg}; border: 1px solid ${t.wordsBorder}; border-radius: 6px; padding: 10px 14px; margin-bottom: 16px; font-size: 11px; color: ${t.wordsText}; }
  .bank-details { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 14px; margin-bottom: 16px; font-size: 11px; }
  .bank-details strong { color: ${t.primary}; font-size: 10px; text-transform: uppercase; letter-spacing: .5px; display: block; margin-bottom: 6px; }
  .bank-grid { display: grid; grid-template-columns: 80px 1fr; gap: 2px 8px; }
  .bk-lbl { color: #64748b; font-weight: 600; }
  .footer { border-top: 1px solid #e2e8f0; padding-top: 16px; display: flex; justify-content: space-between; font-size: 11px; color: #64748b; }
  .sig { text-align: right; }
  .sig p { margin-top: 48px; border-top: 1px solid #94a3b8; padding-top: 4px; }
  .powered-by { text-align: center; margin-top: 18px; font-size: 9px; color: #cbd5e1; letter-spacing: 0.03em; }
  @media print { body { padding: 0; } /* @page margins handle spacing in print */ }
</style>
</head>
<body>
${watermarkHtml}
<table class="header">
  <tr>
    <td style="width:55%;">
      <div class="company">
        ${logoHtml}
        <h1>${esc(profile?.name || 'Your Company')}</h1>
        <p>${esc(addr)}</p>
        ${profile?.gstin ? `<p><strong>GSTIN:</strong> ${esc(profile.gstin)}</p>` : ''}
        ${profile?.cin ? `<p><strong>CIN:</strong> ${esc(profile.cin)}</p>` : ''}
        ${profile?.pan ? `<p><strong>PAN:</strong> ${esc(profile.pan)}</p>` : ''}
        ${profile?.phone ? `<p><strong>Ph:</strong> ${esc(profile.phone)}${profile?.altPhone ? ` / ${esc(profile.altPhone)}` : ''}</p>` : (profile?.altPhone ? `<p><strong>Ph:</strong> ${esc(profile.altPhone)}</p>` : '')}
        ${profile?.email ? `<p><strong>Email:</strong> ${esc(profile.email)}${profile?.altEmail ? ` / ${esc(profile.altEmail)}` : ''}</p>` : (profile?.altEmail ? `<p><strong>Email:</strong> ${esc(profile.altEmail)}</p>` : '')}
      </div>
    </td>
    <td style="width:45%;">
      <div class="invoice-meta">
        <div class="badge">${docLabel}</div>
        <div class="num">${esc(invoice.invoiceNumber)}</div>
        <table>
          <tr><td>Date</td><td><strong>${invoice.date}</strong></td></tr>
          ${invoice.dueDate ? `<tr><td>${dueDateLabel}</td><td><strong>${invoice.dueDate}</strong></td></tr>` : ''}
          ${invoice.referenceNumber ? `<tr><td>${(docType === 'credit_note' || docType === 'debit_note') ? 'Against Invoice' : 'Ref. Invoice'}</td><td><strong style="${(docType === 'credit_note' || docType === 'debit_note') ? 'color:#dc2626;' : ''}">${esc(invoice.referenceNumber)}</strong></td></tr>` : ''}
          ${invoice.placeOfSupply ? `<tr><td>Place of Supply</td><td><strong>${esc(invoice.placeOfSupply)}</strong></td></tr>` : ''}
          ${invoice.isRcm ? `<tr><td>Reverse Charge</td><td><strong style="color:#dc2626;">Applicable</strong></td></tr>` : ''}
          <tr><td>Status</td><td><strong style="text-transform:capitalize;">${esc(invoice.status)}</strong></td></tr>
        </table>
      </div>
    </td>
  </tr>
</table>
<div class="header-spacer"></div>
${(docType === 'credit_note' || docType === 'debit_note') && invoice.referenceNumber ? `<div style="background:#fff1f2;border:1.5px solid #fecdd3;border-radius:6px;padding:8px 14px;margin-bottom:12px;font-size:11px;color:#9f1239;display:flex;align-items:center;gap:8px;"><span style="font-weight:700;">${docType === 'credit_note' ? '📋 Credit Note against Invoice:' : '📋 Debit Note against Invoice:'}</span><span style="font-weight:800;font-size:13px;letter-spacing:0.03em;">${esc(invoice.referenceNumber)}</span></div>` : ''}
<div class="parties">
  <div class="box">
    <h2>Bill To</h2>
    <p><strong>${esc(invoice.partyName)}</strong></p>
    ${invoice.partyGstin ? `<p><strong>GSTIN:</strong> ${esc(invoice.partyGstin)}</p>` : ''}
    ${invoice.partyAddress ? `<p style="white-space:pre-line;">${esc(invoice.partyAddress)}</p>` : ''}
  </div>
  <div class="box">
    <h2>${docType === 'quotation' ? 'Prepared For' : 'Ship To / Delivery'}</h2>
    <p><strong>${esc(invoice.partyName)}</strong></p>
    ${invoice.partyAddress ? `<p style="white-space:pre-line;">${esc(invoice.partyAddress)}</p>` : ''}
  </div>
</div>
<table class="items">
  <thead>${tableHeader}</thead>
  <tbody>${itemRows}</tbody>
</table>
${transportSectionHtml}
${totalsHtml}
${rcmDeclaration}
<div class="words"><strong>Amount in words:</strong> ${toWords(isRcm ? invoice.subtotal : invoice.total)}</div>
${irnHtml}
${bankHtml}
${invoice.termsAndConditions ? `
<div style="margin-top:16px;padding:10px 14px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;font-size:11px;">
  <strong style="color:${t.primary};">Terms &amp; Conditions</strong>
  <p style="margin-top:6px;white-space:pre-line;color:#374151;">${esc(invoice.termsAndConditions)}</p>
</div>` : ''}
<div class="footer">
  <div>
    <p>${docType === 'quotation' ? 'Thank you for the opportunity!' : 'Thank you for your business!'}</p>
    <p style="margin-top:6px;color:#94a3b8;">This is a computer-generated document.</p>
    ${isBOS ? '<p style="margin-top:4px;color:#94a3b8;font-size:10px;">Bill of Supply — No tax charged (exempt/nil-rated supply).</p>' : ''}
  </div>
  <div class="sig">
    <p>For ${esc(profile?.name || 'Your Company')}<br>Authorised Signatory</p>
  </div>
</div>
<div class="powered-by">Powered by CoreLedger Billing v0.2.0 &nbsp;·&nbsp; CoreLedger Technologies PVT LTD</div>
</body>
</html>`;
}

export function buildFreightBillHtml(
  invoice: Invoice,
  profile: CompanyProfile | null,
  watermark = '',
  logoBase64 = ''
): string {
  const t = THEMES.classic;
  const addr = [profile?.addressLine1, profile?.addressLine2, profile?.city, profile?.state, profile?.postalCode]
    .filter(Boolean).join(', ');
  const isIgst = invoice.taxType === 'igst';
  const isRcm = invoice.isRcm === true;

  const wmFontSize = watermark.length <= 5 ? 130 : watermark.length <= 8 ? 110
    : watermark.length <= 12 ? 90 : watermark.length <= 16 ? 72
    : watermark.length <= 22 ? 56 : 44;
  const wmLetterSpacing = watermark.length <= 8 ? 10 : watermark.length <= 16 ? 6 : 3;
  const watermarkHtml = watermark
    ? `<div class="watermark"><span style="font-size:${wmFontSize}px;letter-spacing:${wmLetterSpacing}px;">${esc(watermark)}</span></div>`
    : '';
  const logoHtml = logoBase64
    ? `<img src="${logoBase64}" alt="Logo" style="max-height:60px;max-width:160px;object-fit:contain;margin-bottom:6px;display:block;" />`
    : '';

  const effectiveTrips: InvoiceTrip[] = invoice.trips?.length
    ? invoice.trips
    : (invoice.vehicleNumber || invoice.lrNumber || invoice.fromCity || invoice.toCity)
      ? [{ vehicleNumber: invoice.vehicleNumber, lrNumber: invoice.lrNumber, lrDate: invoice.date, fromCity: invoice.fromCity, toCity: invoice.toCity, driverName: invoice.driverName }]
      : [];

  const hasInvoiceRef = effectiveTrips.some((t) => t.invoiceRef);
  const hasWeight = effectiveTrips.some((t) => t.weight);
  const hasSize = effectiveTrips.some((t) => t.containerSize);
  const totalFreight = effectiveTrips.reduce((s, trip) => s + (trip.freightAmount ?? 0), 0);
  const colSpanSubtotal = 6 + (hasInvoiceRef ? 1 : 0) + (hasWeight ? 1 : 0) + (hasSize ? 1 : 0);

  const lrRows = effectiveTrips.map((trip, i) => `
    <tr>
      <td class="c">${i + 1}</td>
      <td class="c" style="font-weight:700;font-family:monospace;">${esc(trip.lrNumber) || '—'}</td>
      <td class="c">${esc(trip.lrDate) || '—'}</td>
      ${hasInvoiceRef ? `<td class="c">${esc(trip.invoiceRef) || '—'}</td>` : ''}
      <td class="c">${esc(trip.fromCity) || '—'}</td>
      <td class="c">${esc(trip.toCity) || '—'}</td>
      <td class="c" style="font-family:monospace;">${esc(trip.vehicleNumber) || '—'}</td>
      ${hasWeight ? `<td class="c">${esc(trip.weight) || '—'}</td>` : ''}
      ${hasSize ? `<td class="c">${esc(trip.containerSize) || '—'}</td>` : ''}
      <td class="r" style="font-weight:700;">${(trip.freightAmount ?? 0) > 0 ? `₹${fmt(trip.freightAmount!)}` : '—'}</td>
    </tr>`).join('');

  const igstTotal = isRcm ? 0 : invoice.items.reduce((s, i) => s + i.taxableValue * i.gstRate / 100, 0);
  const cgstTotal = isRcm ? 0 : invoice.items.reduce((s, i) => s + i.taxableValue * i.gstRate / 2 / 100, 0);
  const sgstTotal = cgstTotal;
  const gstRates = [...new Set(invoice.items.map((i) => i.gstRate).filter((r) => r > 0))];
  const cgstRate = gstRates.length ? gstRates[0] / 2 : 0;
  const igstRate = gstRates.length ? gstRates[0] : 0;

  const hasBankDetails = profile && (profile.bankName || profile.accountNumber || profile.upiId);
  const bankHtml = hasBankDetails ? `
  <div class="bank-details">
    <strong>Bank Details</strong>
    <div class="bank-grid">
      ${profile.bankName ? `<span class="bk-lbl">Bank</span><span>${esc(profile.bankName)}</span>` : ''}
      ${profile.accountName ? `<span class="bk-lbl">Name</span><span>${esc(profile.accountName)}</span>` : ''}
      ${profile.accountNumber ? `<span class="bk-lbl">A/c No.</span><span style="font-family:monospace;">${esc(profile.accountNumber)}</span>` : ''}
      ${profile.ifscCode ? `<span class="bk-lbl">IFSC</span><span style="font-family:monospace;">${esc(profile.ifscCode)}</span>` : ''}
      ${profile.upiId ? `<span class="bk-lbl">UPI ID</span><span>${esc(profile.upiId)}</span>` : ''}
    </div>
  </div>` : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Freight Bill ${esc(invoice.invoiceNumber)}</title>
<style>
  @page { margin: 18px 24px; }
  :root { color-scheme: light; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 12px; color: #1a1a1a; background: #ffffff; padding: 24px; }
  .watermark { position:fixed; top:18px; left:24px; right:24px; bottom:18px; display:flex; align-items:center; justify-content:center; pointer-events:none; z-index:9999; }
  .watermark span { display:block; width:80%; font-weight:900; color:rgba(0,0,0,0.10); white-space:normal; word-break:break-word; transform:rotate(-35deg); text-transform:uppercase; text-align:center; user-select:none; }
  h1 { font-size: 22px; font-weight: 700; color: ${t.primary}; letter-spacing: 1px; }
  .header { width:100%; border-collapse:collapse; margin-bottom:0; }
  .header td { vertical-align:top; padding-bottom:16px; border-bottom:${t.borderBottom}; }
  .header-spacer { height:24px; }
  .company p { color:#444; line-height:1.5; margin-top:4px; font-size:11px; }
  .invoice-meta { text-align:right; }
  .invoice-meta .badge { background:${t.badgeBg}; color:${t.badgeText}; padding:5px 14px; border-radius:4px; font-size:11px; font-weight:700; letter-spacing:1px; margin-bottom:8px; display:inline-block; }
  .invoice-meta .num { font-size:18px; font-weight:700; color:${t.primary}; margin-bottom:6px; }
  .invoice-meta table { margin-left:auto; border-collapse:collapse; }
  .invoice-meta td { padding:2px 0 2px 12px; }
  .invoice-meta td:first-child { color:#666; font-size:11px; }
  .parties { display:grid; grid-template-columns:1fr 1fr; gap:24px; margin-bottom:20px; }
  .box { background:${t.accentBg}; border:1px solid #e2e8f0; border-radius:6px; padding:12px 14px; }
  .box h2 { color:${t.primary}; font-size:10px; text-transform:uppercase; letter-spacing:.5px; margin-bottom:6px; }
  .box p { color:#1a1a1a; line-height:1.6; }
  table.items { width:100%; border-collapse:collapse; margin-bottom:16px; font-size:11px; }
  table.items th { background:${t.tableHeaderBg}; color:${t.tableHeaderText}; padding:7px 8px; font-size:11px; font-weight:600; }
  table.items td { padding:6px 8px; border-bottom:1px solid #e2e8f0; }
  table.items tr:nth-child(even) td { background:${t.accentBg}; }
  table.items tfoot td { font-weight:700; background:#e8ecf0; border-top:2px solid #cbd5e1; }
  .c { text-align:center; } .r { text-align:right; }
  .totals { display:flex; justify-content:flex-end; margin-bottom:16px; }
  .totals table { border-collapse:collapse; min-width:280px; }
  .totals td { padding:5px 10px; font-size:12px; }
  .totals tr:last-child td { border-top:2px solid ${t.primary}; font-weight:700; font-size:13px; color:${t.primary}; padding-top:8px; }
  .totals td:last-child { text-align:right; }
  .words { background:${t.wordsBg}; border:1px solid ${t.wordsBorder}; border-radius:6px; padding:10px 14px; margin-bottom:16px; font-size:11px; color:${t.wordsText}; }
  .bank-details { background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:10px 14px; margin-bottom:16px; font-size:11px; }
  .bank-details strong { color:${t.primary}; font-size:10px; text-transform:uppercase; letter-spacing:.5px; display:block; margin-bottom:6px; }
  .bank-grid { display:grid; grid-template-columns:80px 1fr; gap:2px 8px; }
  .bk-lbl { color:#64748b; font-weight:600; }
  .rcm-box { margin-bottom:16px; padding:10px 14px; background:#fef2f2; border:1px solid #fca5a5; border-radius:6px; font-size:10.5px; color:#991b1b; }
  .footer { border-top:1px solid #e2e8f0; padding-top:16px; display:flex; justify-content:space-between; font-size:11px; color:#64748b; }
  .sig { text-align:right; }
  .sig p { margin-top:48px; border-top:1px solid #94a3b8; padding-top:4px; }
  .powered-by { text-align:center; margin-top:18px; font-size:9px; color:#cbd5e1; letter-spacing:0.03em; }
  @media print { body { padding:0; } }
</style>
</head>
<body>
${watermarkHtml}
<table class="header">
  <tr>
    <td style="width:55%;">
      <div class="company">
        ${logoHtml}
        <h1>${esc(profile?.name || 'Transport Company')}</h1>
        <p>${esc(addr)}</p>
        ${profile?.gstin ? `<p><strong>GSTIN:</strong> ${esc(profile.gstin)}</p>` : ''}
        ${profile?.cin ? `<p><strong>CIN:</strong> ${esc(profile.cin)}</p>` : ''}
        ${profile?.pan ? `<p><strong>PAN:</strong> ${esc(profile.pan)}</p>` : ''}
        ${profile?.phone ? `<p><strong>Ph:</strong> ${esc(profile.phone)}${profile?.altPhone ? ` / ${esc(profile.altPhone)}` : ''}</p>` : (profile?.altPhone ? `<p><strong>Ph:</strong> ${esc(profile.altPhone)}</p>` : '')}
        ${profile?.email ? `<p><strong>Email:</strong> ${esc(profile.email)}${profile?.altEmail ? ` / ${esc(profile.altEmail)}` : ''}</p>` : (profile?.altEmail ? `<p><strong>Email:</strong> ${esc(profile.altEmail)}</p>` : '')}
      </div>
    </td>
    <td style="width:45%;">
      <div class="invoice-meta">
        <div class="badge">FREIGHT BILL / TAX INVOICE</div>
        <div class="num">${esc(invoice.invoiceNumber)}</div>
        <table>
          <tr><td>Date</td><td><strong>${invoice.date}</strong></td></tr>
          ${invoice.dueDate ? `<tr><td>Due Date</td><td><strong>${invoice.dueDate}</strong></td></tr>` : ''}
          ${invoice.placeOfSupply ? `<tr><td>Place of Supply</td><td><strong>${esc(invoice.placeOfSupply)}</strong></td></tr>` : ''}
          ${invoice.isRcm ? `<tr><td>Reverse Charge</td><td><strong style="color:#dc2626;">Applicable</strong></td></tr>` : ''}
          <tr><td>Status</td><td><strong style="text-transform:capitalize;">${esc(invoice.status)}</strong></td></tr>
        </table>
      </div>
    </td>
  </tr>
</table>
<div class="header-spacer"></div>

<div class="parties">
  <div class="box">
    <h2>Consignor (Shipper)</h2>
    <p><strong>${esc(invoice.partyName)}</strong></p>
    ${invoice.partyGstin ? `<p><strong>GSTIN:</strong> ${esc(invoice.partyGstin)}</p>` : ''}
    ${invoice.partyAddress ? `<p style="white-space:pre-line;">${esc(invoice.partyAddress)}</p>` : ''}
  </div>
  <div class="box">
    <h2>Consignee (Receiver)</h2>
    ${invoice.consigneeName
      ? `<p><strong>${esc(invoice.consigneeName)}</strong></p>
         ${invoice.consigneeGstin ? `<p><strong>GSTIN:</strong> ${esc(invoice.consigneeGstin)}</p>` : ''}
         ${invoice.consigneeAddress ? `<p style="white-space:pre-line;">${esc(invoice.consigneeAddress)}</p>` : ''}`
      : `<p style="color:#94a3b8;font-style:italic;">— Not specified —</p>`}
  </div>
</div>

<table class="items">
  <thead>
    <tr>
      <th class="c" style="width:4%;">No</th>
      <th class="c" style="width:8%;">L/R No</th>
      <th class="c" style="width:10%;">L/R Date</th>
      ${hasInvoiceRef ? `<th class="c" style="width:13%;">Invoice Ref</th>` : ''}
      <th class="c" style="width:10%;">From</th>
      <th class="c" style="width:10%;">To</th>
      <th class="c" style="width:12%;">Vehicle No</th>
      ${hasWeight ? `<th class="c" style="width:8%;">Weight</th>` : ''}
      ${hasSize ? `<th class="c" style="width:8%;">Size</th>` : ''}
      <th class="r" style="width:11%;">Amount</th>
    </tr>
  </thead>
  <tbody>${lrRows}</tbody>
  ${effectiveTrips.length > 1 ? `
  <tfoot>
    <tr>
      <td colspan="${colSpanSubtotal}" class="r" style="padding:6px 8px;">Sub Total</td>
      <td class="r" style="padding:6px 8px;">₹${fmt(totalFreight)}</td>
    </tr>
  </tfoot>` : ''}
</table>

<div class="totals">
  <table>
    <tr><td>Taxable Amount</td><td>₹${fmt(invoice.subtotal)}</td></tr>
    ${isIgst
      ? `<tr><td>IGST ${igstRate}%${isRcm ? ' (RCM)' : ''}</td><td>₹${fmt(igstTotal)}</td></tr>`
      : `<tr><td>CGST ${cgstRate}%${isRcm ? ' (RCM)' : ''}</td><td>₹${fmt(cgstTotal)}</td></tr>
         <tr><td>SGST ${cgstRate}%${isRcm ? ' (RCM)' : ''}</td><td>₹${fmt(sgstTotal)}</td></tr>`}
    <tr><td>Total Amount</td><td>₹${fmt(isRcm ? invoice.subtotal : invoice.total)}</td></tr>
  </table>
</div>

<div class="words"><strong>Amount in words:</strong> ${toWords(isRcm ? invoice.subtotal : invoice.total)}</div>

${isRcm ? `<div class="rcm-box">
  <strong style="display:block;margin-bottom:4px;">⚠ Reverse Charge Declaration</strong>
  Reverse charge is applicable on this supply. Tax is payable by the recipient of the supply as per Section 9(3) / 9(4) of the CGST Act, 2017. The supplier is NOT liable to pay GST on this transaction.
</div>` : ''}

${bankHtml}

${invoice.termsAndConditions ? `
<div style="margin-top:16px;padding:10px 14px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;font-size:11px;margin-bottom:16px;">
  <strong style="color:${t.primary};">Terms &amp; Conditions</strong>
  <p style="margin-top:6px;white-space:pre-line;color:#374151;">${esc(invoice.termsAndConditions)}</p>
</div>` : ''}

<div class="footer">
  <div>
    <p>Thank you for your business!</p>
    <p style="margin-top:6px;color:#94a3b8;">This is a computer-generated document.</p>
  </div>
  <div style="display:flex;gap:40px;align-items:flex-end;">
    <div style="text-align:center;">
      <div style="margin-top:48px;border-top:1px solid #94a3b8;padding-top:4px;font-size:11px;color:#64748b;">Received By</div>
    </div>
    <div class="sig">
      <p>For ${esc(profile?.name || 'Transport Company')}<br>Authorised Signatory</p>
    </div>
  </div>
</div>
<div class="powered-by">Powered by CoreLedger Billing v0.2.0 &nbsp;·&nbsp; CoreLedger Technologies PVT LTD</div>
</body>
</html>`;
}

export function buildLorryReceiptHtml(
  invoice: Invoice,
  profile: CompanyProfile | null,
  watermark = '',
  logoBase64 = ''
): string {
  return buildLrDocument(invoice, null, profile, watermark, logoBase64);
}

export function buildAllIndividualLrsHtml(
  invoice: Invoice,
  profile: CompanyProfile | null,
  watermark = '',
  logoBase64 = ''
): string {
  const trips: InvoiceTrip[] = invoice.trips?.length
    ? invoice.trips
    : (invoice.vehicleNumber || invoice.lrNumber || invoice.fromCity || invoice.toCity)
      ? [{ vehicleNumber: invoice.vehicleNumber, lrNumber: invoice.lrNumber, fromCity: invoice.fromCity, toCity: invoice.toCity, driverName: invoice.driverName }]
      : [];

  if (trips.length <= 1) return buildLrDocument(invoice, null, profile, watermark, logoBase64);

  const t = THEMES.classic;
  const addr = [profile?.addressLine1, profile?.addressLine2, profile?.city, profile?.state, profile?.postalCode].filter(Boolean).join(', ');
  const wmFontSize = watermark.length <= 5 ? 130 : watermark.length <= 8 ? 110 : watermark.length <= 12 ? 90 : watermark.length <= 16 ? 72 : 44;
  const wmLetterSpacing = watermark.length <= 8 ? 10 : watermark.length <= 16 ? 6 : 3;
  const watermarkHtml = watermark ? `<div class="watermark"><span style="font-size:${wmFontSize}px;letter-spacing:${wmLetterSpacing}px;">${esc(watermark)}</span></div>` : '';
  const logoHtml = logoBase64 ? `<img src="${logoBase64}" alt="Logo" style="max-height:60px;max-width:160px;object-fit:contain;margin-bottom:6px;display:block;" />` : '';

  const pages = trips.map((trip, i) => buildLrPageBody(invoice, trip, i, trips.length, profile, logoHtml, addr, t));

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Lorry Receipts — ${esc(invoice.invoiceNumber)}</title>
${lrStyles(t)}
</head>
<body>
${watermarkHtml}
${pages.map((p, i) => `<div class="lr-page"${i < pages.length - 1 ? ' style="page-break-after:always;"' : ''}>${p}</div>`).join('\n')}
<div class="powered-by">Powered by CoreLedger Billing v0.2.0 &nbsp;·&nbsp; CoreLedger Technologies PVT LTD</div>
</body>
</html>`;
}

function lrStyles(t: FormatTheme): string {
  return `<style>
  @page { margin: 18px 24px; }
  :root { color-scheme: light; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 12px; color: #1a1a1a; background: #ffffff; padding: 24px; }
  .watermark { position:fixed; top:18px; left:24px; right:24px; bottom:18px; display:flex; align-items:center; justify-content:center; pointer-events:none; z-index:9999; }
  .watermark span { display:block; width:80%; font-weight:900; color:rgba(0,0,0,0.10); white-space:normal; word-break:break-word; transform:rotate(-35deg); text-transform:uppercase; text-align:center; user-select:none; }
  h1 { font-size:22px; font-weight:700; color:${t.primary}; letter-spacing:1px; }
  .header { width:100%; border-collapse:collapse; }
  .header td { vertical-align:top; padding-bottom:16px; border-bottom:${t.borderBottom}; }
  .header-spacer { height:20px; }
  .company p { color:#444; line-height:1.5; margin-top:4px; font-size:11px; }
  .invoice-meta { text-align:right; }
  .invoice-meta .badge { background:${t.badgeBg}; color:${t.badgeText}; padding:5px 14px; border-radius:4px; font-size:11px; font-weight:700; letter-spacing:1px; margin-bottom:8px; display:inline-block; }
  .invoice-meta .num { font-size:18px; font-weight:700; color:${t.primary}; margin-bottom:6px; }
  .invoice-meta table { margin-left:auto; border-collapse:collapse; }
  .invoice-meta td { padding:2px 0 2px 12px; }
  .invoice-meta td:first-child { color:#666; font-size:11px; }
  .route-box { background:${t.accentBg}; border:1px solid #bae6fd; border-radius:6px; padding:12px 16px; margin-bottom:16px; display:flex; align-items:center; justify-content:space-around; text-align:center; }
  .route-box .city { font-size:17px; font-weight:700; color:${t.primary}; }
  .route-box .lbl { color:#64748b; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.4px; margin-bottom:3px; }
  .route-box .arrow { font-size:26px; color:#0ea5e9; }
  .parties { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:16px; }
  .box { background:${t.accentBg}; border:1px solid #e2e8f0; border-radius:6px; padding:12px 14px; }
  .box h2 { color:${t.primary}; font-size:10px; text-transform:uppercase; letter-spacing:.5px; margin-bottom:6px; }
  .box p { color:#1a1a1a; line-height:1.6; }
  table.items { width:100%; border-collapse:collapse; margin-bottom:16px; font-size:11px; }
  table.items th { background:${t.tableHeaderBg}; color:${t.tableHeaderText}; padding:7px 8px; font-size:11px; font-weight:600; }
  table.items td { padding:6px 8px; border-bottom:1px solid #e2e8f0; }
  table.items tr:nth-child(even) td { background:${t.accentBg}; }
  .c { text-align:center; } .r { text-align:right; }
  .totals { display:flex; justify-content:flex-end; margin-bottom:16px; }
  .totals table { border-collapse:collapse; min-width:260px; }
  .totals td { padding:5px 10px; font-size:12px; }
  .totals tr:last-child td { border-top:2px solid ${t.primary}; font-weight:700; font-size:13px; color:${t.primary}; padding-top:8px; }
  .totals td:last-child { text-align:right; }
  .words { background:${t.wordsBg}; border:1px solid ${t.wordsBorder}; border-radius:6px; padding:10px 14px; margin-bottom:16px; font-size:11px; color:${t.wordsText}; }
  .sig-row { display:grid; grid-template-columns:1fr 1fr 1fr; gap:16px; margin-top:10px; }
  .sig-box { border:1px solid #e2e8f0; border-radius:6px; padding:10px; text-align:center; min-height:60px; display:flex; flex-direction:column; justify-content:space-between; }
  .sig-title { font-size:10px; text-transform:uppercase; color:${t.primary}; font-weight:700; letter-spacing:.4px; }
  .sig-line { border-top:1px solid #94a3b8; margin-top:40px; padding-top:4px; font-size:10px; color:#64748b; }
  .powered-by { text-align:center; margin-top:18px; font-size:9px; color:#cbd5e1; }
  @media print { body { padding:0; } }
</style>`;
}

function buildLrPageBody(
  invoice: Invoice,
  trip: InvoiceTrip,
  tripIdx: number,
  totalTrips: number,
  profile: CompanyProfile | null,
  logoHtml: string,
  addr: string,
  t: FormatTheme
): string {
  const lrNum = trip.lrNumber ?? invoice.lrNumber ?? invoice.invoiceNumber;
  const vehicle = trip.vehicleNumber ?? invoice.vehicleNumber;
  const driver = trip.driverName ?? invoice.driverName;
  const fromCity = trip.fromCity ?? invoice.fromCity;
  const toCity = trip.toCity ?? invoice.toCity;
  const lrDate = trip.lrDate ?? invoice.date;

  const descCell = (item: typeof invoice.items[0]) => {
    const desc = esc(item.description) || '<span style="color:#94a3b8;font-style:italic;">—</span>';
    return item.notes ? `${desc}<br><span style="font-size:9px;color:#64748b;">${esc(item.notes)}</span>` : desc;
  };

  const itemRows = invoice.items.map((item, i) => `
    <tr>
      <td class="c">${i + 1}</td>
      <td>${descCell(item)}</td>
      <td class="c">${esc(item.hsnSac)}</td>
      <td class="r">${item.qty}</td>
      <td class="r">₹${fmt(item.rate)}</td>
      <td class="r">₹${fmt(item.taxableValue)}</td>
    </tr>`).join('');

  const freightAmount = (trip.freightAmount ?? 0) > 0 ? trip.freightAmount! : invoice.total;

  return `
<table class="header">
  <tr>
    <td style="width:55%;">
      <div class="company">
        ${logoHtml}
        <h1>${esc(profile?.name || 'Transport Company')}</h1>
        <p>${esc(addr)}</p>
        ${profile?.gstin ? `<p><strong>GSTIN:</strong> ${esc(profile.gstin)}</p>` : ''}
        ${profile?.cin ? `<p><strong>CIN:</strong> ${esc(profile.cin)}</p>` : ''}
        ${profile?.pan ? `<p><strong>PAN:</strong> ${esc(profile.pan)}</p>` : ''}
        ${profile?.phone ? `<p><strong>Ph:</strong> ${esc(profile.phone)}${profile?.altPhone ? ` / ${esc(profile.altPhone)}` : ''}</p>` : (profile?.altPhone ? `<p><strong>Ph:</strong> ${esc(profile.altPhone)}</p>` : '')}
        ${profile?.email ? `<p><strong>Email:</strong> ${esc(profile.email)}${profile?.altEmail ? ` / ${esc(profile.altEmail)}` : ''}</p>` : (profile?.altEmail ? `<p><strong>Email:</strong> ${esc(profile.altEmail)}</p>` : '')}
      </div>
    </td>
    <td style="width:45%;">
      <div class="invoice-meta">
        <div class="badge">LORRY RECEIPT</div>
        <div class="num">LR No: ${esc(lrNum)}</div>
        <table>
          <tr><td>Date</td><td><strong>${lrDate}</strong></td></tr>
          ${vehicle ? `<tr><td>Vehicle No.</td><td><strong style="font-family:monospace;">${esc(vehicle)}</strong></td></tr>` : ''}
          ${invoice.placeOfSupply ? `<tr><td>Place of Supply</td><td><strong>${esc(invoice.placeOfSupply)}</strong></td></tr>` : ''}
          ${totalTrips > 1 ? `<tr><td>LR</td><td><strong>${tripIdx + 1} of ${totalTrips}</strong></td></tr>` : ''}
        </table>
      </div>
    </td>
  </tr>
</table>
<div class="header-spacer"></div>

<div class="route-box">
  <div><div class="lbl">From</div><div class="city">${esc(fromCity ?? '—')}</div></div>
  <div class="arrow">→</div>
  <div><div class="lbl">To</div><div class="city">${esc(toCity ?? '—')}</div></div>
</div>

<div class="parties">
  <div class="box">
    <h2>Consignor (Shipper)</h2>
    <p><strong>${esc(invoice.partyName)}</strong></p>
    ${invoice.partyGstin ? `<p><strong>GSTIN:</strong> ${esc(invoice.partyGstin)}</p>` : ''}
    ${invoice.partyAddress ? `<p style="white-space:pre-line;">${esc(invoice.partyAddress)}</p>` : ''}
  </div>
  <div class="box">
    <h2>Consignee (Receiver)</h2>
    ${(trip.consignee || invoice.consigneeName)
      ? `<p><strong>${esc(trip.consignee || invoice.consigneeName!)}</strong></p>
         ${(trip.consigneeGstin || invoice.consigneeGstin) ? `<p><strong>GSTIN:</strong> ${esc(trip.consigneeGstin || invoice.consigneeGstin!)}</p>` : ''}
         ${(trip.consigneeAddress || invoice.consigneeAddress) ? `<p style="white-space:pre-line;">${esc(trip.consigneeAddress || invoice.consigneeAddress!)}</p>` : ''}`
      : `<p style="color:#94a3b8;font-style:italic;">— Not specified —</p>`}
  </div>
</div>
<div class="box" style="margin-bottom:16px;">
  <h2>Vehicle / Driver</h2>
  <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;">
    ${vehicle ? `<div><strong>Vehicle:</strong> ${esc(vehicle)}</div>` : ''}
    ${driver ? `<div><strong>Driver:</strong> ${esc(driver)}</div>` : ''}
    ${trip.weight ? `<div><strong>Weight:</strong> ${esc(trip.weight)}</div>` : ''}
    ${trip.containerSize ? `<div><strong>Size:</strong> ${esc(trip.containerSize)}</div>` : ''}
    ${trip.invoiceRef ? `<div><strong>Invoice Ref:</strong> ${esc(trip.invoiceRef)}</div>` : ''}
  </div>
</div>

<table class="items">
  <thead>
    <tr>
      <th class="c" style="width:5%">#</th>
      <th style="text-align:left;width:38%">Description of Goods</th>
      <th class="c" style="width:10%">HSN/SAC</th>
      <th class="r" style="width:8%">Qty</th>
      <th class="r" style="width:14%">Rate</th>
      <th class="r" style="width:15%">Amount</th>
    </tr>
  </thead>
  <tbody>${itemRows}</tbody>
</table>

<div class="totals">
  <table>
    <tr style="font-weight:700;"><td>Total Freight</td><td>₹${fmt(freightAmount)}</td></tr>
  </table>
</div>

${invoice.termsAndConditions ? `
<div style="margin-bottom:16px;padding:10px 14px;background:${t.wordsBg};border:1px solid ${t.wordsBorder};border-radius:6px;font-size:11px;">
  <strong style="color:${t.primary};">Terms &amp; Conditions</strong>
  <p style="margin-top:6px;white-space:pre-line;color:#374151;">${esc(invoice.termsAndConditions)}</p>
</div>` : ''}

<div class="sig-row">
  <div class="sig-box"><div class="sig-title">Consignor's Signature</div><div class="sig-line">Sender</div></div>
  <div class="sig-box"><div class="sig-title">Driver's Signature</div><div class="sig-line">Driver / Transporter</div></div>
  <div class="sig-box"><div class="sig-title">Consignee's Signature</div><div class="sig-line">Receiver</div></div>
</div>`;
}

function buildLrDocument(
  invoice: Invoice,
  _tripIndex: number | null,
  profile: CompanyProfile | null,
  watermark: string,
  logoBase64: string
): string {
  const t = THEMES.classic;
  const addr = [profile?.addressLine1, profile?.addressLine2, profile?.city, profile?.state, profile?.postalCode].filter(Boolean).join(', ');
  const wmFontSize = watermark.length <= 5 ? 130 : watermark.length <= 8 ? 110 : watermark.length <= 12 ? 90 : watermark.length <= 16 ? 72 : 44;
  const wmLetterSpacing = watermark.length <= 8 ? 10 : watermark.length <= 16 ? 6 : 3;
  const watermarkHtml = watermark ? `<div class="watermark"><span style="font-size:${wmFontSize}px;letter-spacing:${wmLetterSpacing}px;">${esc(watermark)}</span></div>` : '';
  const logoHtml = logoBase64 ? `<img src="${logoBase64}" alt="Logo" style="max-height:60px;max-width:160px;object-fit:contain;margin-bottom:6px;display:block;" />` : '';

  const lrTrips: InvoiceTrip[] = invoice.trips?.length
    ? invoice.trips
    : (invoice.vehicleNumber || invoice.lrNumber || invoice.fromCity || invoice.toCity)
      ? [{ vehicleNumber: invoice.vehicleNumber, lrNumber: invoice.lrNumber, fromCity: invoice.fromCity, toCity: invoice.toCity, driverName: invoice.driverName }]
      : [];

  const trip0 = lrTrips[0] ?? {};
  const bodyHtml = lrTrips.length > 1
    ? buildLrMultiTripBody(invoice, lrTrips, profile, logoHtml, addr, t)
    : buildLrPageBody(invoice, trip0, 0, 1, profile, logoHtml, addr, t);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Lorry Receipt ${esc(invoice.lrNumber ?? invoice.invoiceNumber)}</title>
${lrStyles(t)}
</head>
<body>
${watermarkHtml}
${bodyHtml}
<div class="powered-by">Powered by CoreLedger Billing v0.2.0 &nbsp;·&nbsp; CoreLedger Technologies PVT LTD</div>
</body>
</html>`;
}

function buildLrMultiTripBody(
  invoice: Invoice,
  lrTrips: InvoiceTrip[],
  profile: CompanyProfile | null,
  logoHtml: string,
  addr: string,
  t: FormatTheme
): string {
  const descCell = (item: typeof invoice.items[0]) => {
    const desc = esc(item.description) || '<span style="color:#94a3b8;font-style:italic;">—</span>';
    return item.notes ? `${desc}<br><span style="font-size:9px;color:#64748b;">${esc(item.notes)}</span>` : desc;
  };
  const itemRows = invoice.items.map((item, i) => `
    <tr>
      <td class="c">${i + 1}</td>
      <td>${descCell(item)}</td>
      <td class="c">${esc(item.hsnSac)}</td>
      <td class="r">${item.qty}</td>
      <td class="r">₹${fmt(item.rate)}</td>
      <td class="r">₹${fmt(item.taxableValue)}</td>
    </tr>`).join('');

  const lrNumDisplay = lrTrips.map((t) => esc(t.lrNumber) || '—').join(', ');
  const vehicleDisplay = [...new Set(lrTrips.map((t) => t.vehicleNumber).filter(Boolean))].join(', ');

  return `
<table class="header">
  <tr>
    <td style="width:55%;">
      <div class="company">
        ${logoHtml}
        <h1>${esc(profile?.name || 'Transport Company')}</h1>
        <p>${esc(addr)}</p>
        ${profile?.gstin ? `<p><strong>GSTIN:</strong> ${esc(profile.gstin)}</p>` : ''}
        ${profile?.cin ? `<p><strong>CIN:</strong> ${esc(profile.cin)}</p>` : ''}
        ${profile?.pan ? `<p><strong>PAN:</strong> ${esc(profile.pan)}</p>` : ''}
        ${profile?.phone ? `<p><strong>Ph:</strong> ${esc(profile.phone)}${profile?.altPhone ? ` / ${esc(profile.altPhone)}` : ''}</p>` : (profile?.altPhone ? `<p><strong>Ph:</strong> ${esc(profile.altPhone)}</p>` : '')}
        ${profile?.email ? `<p><strong>Email:</strong> ${esc(profile.email)}${profile?.altEmail ? ` / ${esc(profile.altEmail)}` : ''}</p>` : (profile?.altEmail ? `<p><strong>Email:</strong> ${esc(profile.altEmail)}</p>` : '')}
      </div>
    </td>
    <td style="width:45%;">
      <div class="invoice-meta">
        <div class="badge">LORRY RECEIPT</div>
        <div class="num" style="font-size:14px;">${lrNumDisplay}</div>
        <table>
          <tr><td>Date</td><td><strong>${invoice.date}</strong></td></tr>
          ${vehicleDisplay ? `<tr><td>Vehicle(s)</td><td><strong style="font-family:monospace;">${esc(vehicleDisplay)}</strong></td></tr>` : ''}
          ${invoice.placeOfSupply ? `<tr><td>Place of Supply</td><td><strong>${esc(invoice.placeOfSupply)}</strong></td></tr>` : ''}
        </table>
      </div>
    </td>
  </tr>
</table>
<div class="header-spacer"></div>

<div class="box" style="margin-bottom:16px;">
  <h2>Trip Details (${lrTrips.length} Lorries)</h2>
  <table style="width:100%;border-collapse:collapse;font-size:11px;margin-top:8px;">
    <thead>
      <tr style="background:#f0f9ff;">
        <th style="padding:5px 8px;text-align:center;border-bottom:1px solid #bae6fd;">#</th>
        <th style="padding:5px 8px;text-align:left;border-bottom:1px solid #bae6fd;">LR No.</th>
        <th style="padding:5px 8px;text-align:left;border-bottom:1px solid #bae6fd;">LR Date</th>
        <th style="padding:5px 8px;text-align:left;border-bottom:1px solid #bae6fd;">Vehicle No.</th>
        <th style="padding:5px 8px;text-align:left;border-bottom:1px solid #bae6fd;">From</th>
        <th style="padding:5px 8px;text-align:left;border-bottom:1px solid #bae6fd;">To</th>
        <th style="padding:5px 8px;text-align:left;border-bottom:1px solid #bae6fd;">Driver</th>
        <th style="padding:5px 8px;text-align:right;border-bottom:1px solid #bae6fd;">Freight (₹)</th>
      </tr>
    </thead>
    <tbody>
      ${lrTrips.map((tr, i) => `
      <tr style="${i % 2 === 1 ? 'background:#f0f9ff;' : ''}">
        <td style="padding:4px 8px;text-align:center;">${i + 1}</td>
        <td style="padding:4px 8px;font-family:monospace;font-weight:600;">${esc(tr.lrNumber) || '—'}</td>
        <td style="padding:4px 8px;">${esc(tr.lrDate) || '—'}</td>
        <td style="padding:4px 8px;font-family:monospace;">${esc(tr.vehicleNumber) || '—'}</td>
        <td style="padding:4px 8px;">${esc(tr.fromCity) || '—'}</td>
        <td style="padding:4px 8px;">${esc(tr.toCity) || '—'}</td>
        <td style="padding:4px 8px;">${esc(tr.driverName) || '—'}</td>
        <td style="padding:4px 8px;text-align:right;">${(tr.freightAmount ?? 0) > 0 ? `₹${fmt(tr.freightAmount!)}` : '—'}</td>
      </tr>`).join('')}
    </tbody>
    ${lrTrips.length > 1 ? `<tfoot><tr style="font-weight:700;background:#e0f2fe;">
      <td colspan="7" style="padding:5px 8px;text-align:right;border-top:1px solid #7dd3fc;">Total Freight</td>
      <td style="padding:5px 8px;text-align:right;border-top:1px solid #7dd3fc;">₹${fmt(lrTrips.reduce((s, tr) => s + (tr.freightAmount ?? 0), 0))}</td>
    </tr></tfoot>` : ''}
  </table>
</div>

<div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;">
  <div class="box">
    <h2>Consignor (Shipper)</h2>
    <p style="margin-top:6px;"><strong>${esc(invoice.partyName)}</strong></p>
    ${invoice.partyGstin ? `<p style="color:#475569;">GSTIN: ${esc(invoice.partyGstin)}</p>` : ''}
    ${invoice.partyAddress ? `<p style="color:#475569;white-space:pre-line;">${esc(invoice.partyAddress)}</p>` : ''}
  </div>
  <div class="box">
    <h2>Consignee (Receiver)</h2>
    ${(invoice.consigneeName || lrTrips.some((t) => t.consignee))
      ? `<p style="margin-top:6px;"><strong>${esc(lrTrips.find((t) => t.consignee)?.consignee ?? invoice.consigneeName!)}</strong></p>
         ${(lrTrips.find((t) => t.consigneeGstin)?.consigneeGstin || invoice.consigneeGstin) ? `<p style="color:#475569;">GSTIN: ${esc(lrTrips.find((t) => t.consigneeGstin)?.consigneeGstin ?? invoice.consigneeGstin!)}</p>` : ''}
         ${(lrTrips.find((t) => t.consigneeAddress)?.consigneeAddress || invoice.consigneeAddress) ? `<p style="color:#475569;white-space:pre-line;">${esc(lrTrips.find((t) => t.consigneeAddress)?.consigneeAddress ?? invoice.consigneeAddress!)}</p>` : ''}`
      : `<p style="margin-top:6px;color:#94a3b8;font-style:italic;">— Not specified —</p>`}
  </div>
</div>

<table class="items">
  <thead>
    <tr>
      <th class="c" style="width:5%">#</th>
      <th style="text-align:left;width:38%">Description of Goods</th>
      <th class="c" style="width:10%">HSN/SAC</th>
      <th class="r" style="width:8%">Qty</th>
      <th class="r" style="width:14%">Rate</th>
      <th class="r" style="width:15%">Amount</th>
    </tr>
  </thead>
  <tbody>${itemRows}</tbody>
</table>

<div class="totals">
  <table>
    <tr style="font-weight:700;"><td>Total Freight</td><td>₹${fmt(invoice.total)}</td></tr>
  </table>
</div>

${invoice.termsAndConditions ? `
<div style="margin-bottom:16px;padding:10px 14px;background:${t.wordsBg};border:1px solid ${t.wordsBorder};border-radius:6px;font-size:11px;">
  <strong style="color:${t.primary};">Terms &amp; Conditions</strong>
  <p style="margin-top:6px;white-space:pre-line;color:#374151;">${esc(invoice.termsAndConditions)}</p>
</div>` : ''}

<div class="sig-row">
  <div class="sig-box"><div class="sig-title">Consignor's Signature</div><div class="sig-line">Sender</div></div>
  <div class="sig-box"><div class="sig-title">Driver's Signature</div><div class="sig-line">Driver / Transporter</div></div>
  <div class="sig-box"><div class="sig-title">Consignee's Signature</div><div class="sig-line">Receiver</div></div>
</div>`;
}
